/**
 * AGENTS PAGE
 *
 * Wraps the Board (AgentView) and List (MyAgentsListView) views
 * with a shared header bar that includes a view toggle.
 */

import {
  LayoutList,
  LayoutGrid,
  Filter,
  SortAsc,
} from "lucide-react";
import { useUIStore } from "@/stores/uiStore";
import { useUnifiedAgents } from "@/hooks/useUnifiedAgents";
import { AgentView } from "./AgentView";
import { MyAgentsListView } from "./MyAgentsListView";

export function AgentsPage() {
  const agentsViewMode = useUIStore((s) => s.agentsViewMode);
  const setAgentsViewMode = useUIStore((s) => s.setAgentsViewMode);
  const groups = useUnifiedAgents();
  const totalAgents = groups.reduce((n, g) => n + g.rows.length, 0);
  const isListMode = agentsViewMode === "list";

  // List view manages its own header (includes group-by controls)
  if (isListMode) {
    return (
      <MyAgentsListView
        viewMode={agentsViewMode}
        onViewModeChange={setAgentsViewMode}
      />
    );
  }

  // Board mode — header + AgentView
  return (
    <div className="flex flex-col flex-1 overflow-hidden min-w-0">
      {/* Page Header */}
      <div className="flex items-center h-12 px-5 border-b border-[rgba(255,255,255,0.06)] shrink-0">
        <div className="flex items-center gap-2.5 flex-1">
          <span className="text-[16px] font-semibold text-colony-text-heading tracking-[-0.015em]">
            My Agents
          </span>
          <span className="text-[11px] text-colony-text-tertiary font-mono tabular-nums">
            {totalAgents}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {/* View toggle */}
          <ViewToggle active={agentsViewMode} onChange={setAgentsViewMode} />

          {/* Filter (stubbed) */}
          <button className="flex items-center gap-1.5 h-7 px-2.5 rounded-[6px] text-[12px] font-medium text-colony-text-tertiary hover:text-colony-text-secondary hover:bg-[rgba(255,255,255,0.04)] transition-all cursor-pointer">
            <Filter size={13} strokeWidth={1.5} />
            Filter
          </button>

          {/* Sort (stubbed) */}
          <button className="flex items-center gap-1.5 h-7 px-2.5 rounded-[6px] text-[12px] font-medium text-colony-text-tertiary hover:text-colony-text-secondary hover:bg-[rgba(255,255,255,0.04)] transition-all cursor-pointer">
            <SortAsc size={13} strokeWidth={1.5} />
            Sort
          </button>
        </div>
      </div>

      {/* Board View */}
      <div className="flex-1 overflow-hidden">
        <AgentView />
      </div>
    </div>
  );
}

function ViewToggle({ active, onChange }: { active: "list" | "board"; onChange: (mode: "list" | "board") => void }) {
  return (
    <div className="flex items-center h-7 rounded-[6px] bg-[#1A1A1E] border border-[rgba(255,255,255,0.06)] overflow-hidden mr-2" role="group" aria-label="View mode">
      <button
        onClick={() => onChange("list")}
        aria-label="List view"
        aria-pressed={active === "list"}
        className={`flex items-center justify-center w-7 h-full transition-colors cursor-pointer ${
          active === "list"
            ? "text-colony-text-primary bg-[#272730]"
            : "text-colony-text-tertiary hover:text-colony-text-secondary"
        }`}
      >
        <LayoutList size={14} strokeWidth={1.5} aria-hidden="true" />
      </button>
      <button
        onClick={() => onChange("board")}
        aria-label="Board view"
        aria-pressed={active === "board"}
        className={`flex items-center justify-center w-7 h-full transition-colors cursor-pointer ${
          active === "board"
            ? "text-colony-text-primary bg-[#272730]"
            : "text-colony-text-tertiary hover:text-colony-text-secondary"
        }`}
      >
        <LayoutGrid size={14} strokeWidth={1.5} aria-hidden="true" />
      </button>
    </div>
  );
}
